import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import type { UserProfile } from '@types';
import SupportChatModal, { type SupportChatRecord } from './SupportChatModal';

const displayDate = (value: unknown) => formatLocalDateTime(value, 'Pending');
const displaySummary = (chat: SupportChatRecord) => !chat.summary || chat.summary === 'Summary is being prepared.' ? chat.lastMessagePreview : chat.summary;

const OrganizationChatsPanel: React.FC<{ organizationId: string; organizationRegion: string; currentUser: UserProfile; openChatId?: string | null; onOpenHandled?: () => void }> = ({ organizationId, organizationRegion, currentUser, openChatId, onOpenHandled }) => {
    const [chats, setChats] = useState<SupportChatRecord[]>([]);
    const [selected, setSelected] = useState<SupportChatRecord | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const constraints = [where('organizationId', '==', organizationId)];
        if (currentUser.adminRole === 'Regional')
            constraints.push(where('managerUid', '==', currentUser.uid));
        const chatsQuery = query(collection(db, 'support_chats'), ...constraints, orderBy('lastMessageAt', 'desc'));
        return onSnapshot(chatsQuery, snapshot => {
            const next = snapshot.docs.map(document => ({ id: document.id, ...document.data() } as SupportChatRecord));
            setChats(next);
            setSelected(previous => previous ? next.find(chat => chat.id === previous.id) || previous : null);
        }, snapshotError => setError(snapshotError.message));
    }, [organizationId, organizationRegion, currentUser.adminRole, currentUser.uid]);

    useEffect(() => {
        if (!openChatId || !chats.length)
            return;
        const match = chats.find(chat => chat.id === openChatId);
        if (match)
            setSelected(match);
        onOpenHandled?.();
    }, [openChatId, chats]);

    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5"><h3 className="font-extrabold text-slate-900">Organization Chats</h3><p className="mt-1 text-xs text-slate-500">Client conversations and generated summaries.</p></div>
            {error && <p className="m-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-5 py-3">Summary</th><th className="px-5 py-3">Client User</th><th className="px-5 py-3">Manager</th><th className="px-5 py-3">Started</th><th className="px-5 py-3">Last Message</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {chats.map(chat => (
                            <tr key={chat.id} onClick={() => setSelected(chat)} className="cursor-pointer hover:bg-blue-50/50">
                                <td className="max-w-sm px-5 py-4"><p className="font-semibold text-slate-800">{displaySummary(chat)}</p>{chat.unreadForManager && <span className="mt-1 inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">New message</span>}</td>
                                <td className="px-5 py-4"><p className="font-semibold text-slate-800">{chat.participantName}</p><p className="text-xs text-slate-400">{chat.participantEmail}</p></td>
                                <td className="px-5 py-4 text-slate-600">{chat.managerName}</td>
                                <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{displayDate(chat.startedAt)}</td>
                                <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500">{displayDate(chat.lastMessageAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!chats.length && !error && <p className="p-10 text-center text-sm text-slate-400">No chats with this organization.</p>}
            </div>
            {selected && <SupportChatModal mode="admin" chat={selected} onClose={() => setSelected(null)} />}
        </section>
    );
};

export default OrganizationChatsPanel;
